/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
/* eslint-disable no-param-reassign */
import React from 'react';
import {
  $getRoot,
  $isElementNode,
  DecoratorNode,
  LexicalNode,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {
  IInlineBox, InlineBoxType,
} from '$/components/InlineBox/types';
import InlineboxEditer from './InlineboxEditer';
import MentionToken from './MentionToken';
import { isPostTaskInlineBoxData } from './postTaskLabel';
import styles from './index.module.scss';
import { MentionValue } from './types';

export const INLINE_BOX_NODE_TYPE = 'roboneo-inline-box';
export const MENTION_NODE_TYPE = 'roboneo-mention';

type SerializedInlineBoxNode = Spread<{
  type: typeof INLINE_BOX_NODE_TYPE;
  version: 1;
  inlineBox: IInlineBox;
}, SerializedLexicalNode>;

type SerializedMentionNode = Spread<{
  type: typeof MENTION_NODE_TYPE;
  version: 1;
  mention: MentionValue;
}, SerializedLexicalNode>;

export const getInlineBoxId = (inlineBox: IInlineBox) => {
  return inlineBox.data.index;
};

export class InlineBoxNode extends DecoratorNode<JSX.Element> {
  __inlineBox: IInlineBox;

  __inlineId: string;

  __onRemove?: () => void;

  static getType(): string {
    return INLINE_BOX_NODE_TYPE;
  }

  static clone(node: InlineBoxNode): InlineBoxNode {
    return new InlineBoxNode(node.__inlineBox, node.__inlineId, node.__onRemove, node.__key);
  }

  static importJSON(serializedNode: SerializedInlineBoxNode): InlineBoxNode {
    return new InlineBoxNode(
      serializedNode.inlineBox,
      getInlineBoxId(serializedNode.inlineBox),
    );
  }

  exportJSON(): SerializedInlineBoxNode {
    return {
      type: INLINE_BOX_NODE_TYPE,
      version: 1,
      inlineBox: this.__inlineBox,
    };
  }

  constructor(inlineBox: IInlineBox, inlineId: string, onRemove?: () => void, key?: string) {
    super(key);
    this.__inlineBox = inlineBox;
    this.__inlineId = inlineId;
    this.__onRemove = onRemove;
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('span');
    dom.classList.add('inlineBoxContainer');
    // 外层样式（vertical-align / line-height）用于缓解 Safari contenteditable 中行内装饰旁光标高度异常
    dom.classList.add(styles['inline-box-container']);
    dom.setAttribute('contenteditable', 'false');

    return dom;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    // 选中/加载态不会以 prop 形式传入，InlineboxEditer 订阅 inlineBoxStateStore 自行拼出 config，
    // 以避免修改 LexicalNode 给 history 引入多余条目。
    return <InlineboxEditer inlineBox={this.__inlineBox} nodeKey={this.getKey()} />;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return !isPostTaskInlineBoxData(this.__inlineBox.data);
  }

  destroy(): void {
    // onRemove 由 InputTextArea 中 registerMutationListener(destroyed) + prevEditorState 统一触发，
    // 因部分删除路径下 DecoratorNode.destroy 不会可靠执行。
    if (this.__inlineBox.type === InlineBoxType.Mark) {
      const root = $getRoot();
      const collect = (node: LexicalNode, list: LexicalNode[]) => {
        list.push(node);
        if ($isElementNode(node)) {
          node.getChildren().forEach((child) => collect(child, list));
        }
      };
      const nodes: LexicalNode[] = [];
      root.getChildren().forEach((child) => collect(child, nodes));
      const markNodes = nodes.filter(
        (node) => $isInlineBoxNode(node) && node.__inlineBox?.type === InlineBoxType.Mark,
      ) as InlineBoxNode[];
      markNodes.forEach((node, index) => {
        const writable = node.getWritable() as InlineBoxNode;
        writable.__inlineBox = {
          ...writable.__inlineBox,
          data: {
            ...writable.__inlineBox.data,
            order: index + 1,
          },
        };
      });
    }
  }

  getInlineId(): string {
    return this.__inlineId;
  }
}

export class MentionNode extends DecoratorNode<JSX.Element> {
  __mention: MentionValue;

  static getType(): string {
    return MENTION_NODE_TYPE;
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode({ ...node.__mention }, node.__key);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode({ ...serializedNode.mention });
  }

  constructor(mention: MentionValue, key?: string) {
    super(key);
    this.__mention = { ...mention };
  }

  exportJSON(): SerializedMentionNode {
    return {
      type: MENTION_NODE_TYPE,
      version: 1,
      mention: { ...this.__mention },
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('span');
    dom.setAttribute('contenteditable', 'false');
    // 与 InlineBox 装饰节点一致，收紧 WebKit 下行盒避免 @ 提及旁光标被拉高
    dom.classList.add('RoboneoChatMentionContainer');
    dom.classList.add(styles['mention-container']);
    return dom;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <MentionToken id={this.__mention.id} name={this.__mention.name} />;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return false;
  }

  getMention(): MentionValue {
    return { ...this.__mention };
  }
}

export const $createInlineBoxNode = (inlineBox: IInlineBox, onRemove?: () => void) => {
  const inlineId = getInlineBoxId(inlineBox);
  return new InlineBoxNode(inlineBox, inlineId, onRemove);
};

export const $createMentionNode = (mention: MentionValue): MentionNode => {
  return new MentionNode(mention);
};

export const $isInlineBoxNode = (node: LexicalNode | null | undefined): node is InlineBoxNode => {
  return node instanceof InlineBoxNode;
};

export const $isMentionNode = (node: LexicalNode | null | undefined): node is MentionNode => {
  return node instanceof MentionNode;
};
